import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { showToast } from '@/components/ui/toast';

import { useApi } from './ApiContext';
import { useAuth } from './AuthContext';

// Types
export interface NotificationItem {
  _id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  userId?: string;
}

export interface ToastConfig {
  position?:
    | 'top-right'
    | 'top-center'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'bottom-left';
  duration?: number;
  dismissible?: boolean;
}

export interface NotificationContextType {
  // Toast notifications
  showSuccess: (message: string, config?: ToastConfig) => void;
  showError: (message: string, config?: ToastConfig) => void;
  showInfo: (message: string, config?: ToastConfig) => void;
  showWarning: (message: string, config?: ToastConfig) => void;

  // App notifications
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;

  // Actions
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotifications: () => void;

  // Real-time updates
  addNotification: (notification: NotificationItem) => void;
  removeNotification: (notificationId: string) => void;
}

// Create the context
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

// Custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};

// NotificationProvider component
interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { get, put } = useApi();
  const { isAuthenticated } = useAuth();

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Toast notification methods
  const showSuccess = useCallback(
    (message: string, config: ToastConfig = {}) => {
      const defaultConfig: ToastConfig = {
        position: 'top-right',
        duration: 5000,
        dismissible: true,
        ...config,
      };
      showToast(message, true, defaultConfig);
    },
    []
  );

  const showError = useCallback((message: string, config: ToastConfig = {}) => {
    const defaultConfig: ToastConfig = {
      position: 'top-right',
      duration: 7000,
      dismissible: true,
      ...config,
    };
    showToast(message, false, defaultConfig);
  }, []);

  const showInfo = useCallback((message: string, config: ToastConfig = {}) => {
    const defaultConfig: ToastConfig = {
      position: 'top-right',
      duration: 5000,
      dismissible: true,
      ...config,
    };
    showToast(message, true, defaultConfig);
  }, []);

  const showWarning = useCallback(
    (message: string, config: ToastConfig = {}) => {
      const defaultConfig: ToastConfig = {
        position: 'top-right',
        duration: 6000,
        dismissible: true,
        ...config,
      };
      showToast(message, false, defaultConfig);
    },
    []
  );

  // Fetch notifications from API (only if authenticated)
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await get<{
        notifications: NotificationItem[];
        pagination: any;
      }>('/notifications', { limit: 50 });

      if (response.success && response.data) {
        setNotifications(response.data.notifications || []);
      }
    } catch (error) {
      // Use showToast directly instead of showError to break circular dependency
      showToast('Failed to fetch notifications', false, {
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  }, [get, isAuthenticated]);

  // Mark notification as read (only if authenticated)
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!isAuthenticated) {
        return;
      }

      try {
        const response = await put(`/notifications/${notificationId}/read`);

        if (response.success) {
          setNotifications(prev =>
            prev.map(notification =>
              notification._id === notificationId
                ? { ...notification, isRead: true }
                : notification
            )
          );
        }
      } catch (error) {
        // Use showToast directly instead of showError to break circular dependency
        showToast('Failed to mark notification as read', false, {
          position: 'top-right',
        });
      }
    },
    [put, isAuthenticated]
  );

  // Mark all notifications as read (only if authenticated)
  const markAllAsRead = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);

      // Mark all unread notifications as read locally first (optimistic update)
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, isRead: true }))
      );

      // Then make API calls for each unread notification
      const promises = unreadNotifications.map(notification =>
        put(`/notifications/${notification._id}/read`)
      );

      await Promise.allSettled(promises);
    } catch (error) {
      // Use showToast directly instead of showError to break circular dependency
      showToast('Failed to mark all notifications as read', false, {
        position: 'top-right',
      });
      // Revert optimistic update on error
      fetchNotifications();
    }
  }, [notifications, put, fetchNotifications, isAuthenticated]);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Add a new notification (for real-time updates)
  const addNotification = useCallback(
    (notification: NotificationItem) => {
      setNotifications(prev => [notification, ...prev]);

      // Show toast for new notification
      switch (notification.type) {
        case 'success':
          showSuccess(notification.message);
          break;
        case 'error':
          showError(notification.message);
          break;
        case 'warning':
          showWarning(notification.message);
          break;
        default:
          showInfo(notification.message);
      }
    },
    [showSuccess, showError, showWarning, showInfo]
  );

  // Remove a notification
  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n._id !== notificationId));
  }, []);

  // Load notifications when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      // Clear notifications when not authenticated
      setNotifications([]);
    }
  }, [isAuthenticated]); // Remove fetchNotifications from dependency array to prevent infinite loop

  // Auto-refresh notifications every 5 minutes (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const interval = setInterval(
      () => {
        fetchNotifications();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]); // Remove fetchNotifications from dependency array to prevent infinite loop

  // Context value
  const value: NotificationContextType = {
    // Toast notifications
    showSuccess,
    showError,
    showInfo,
    showWarning,

    // App notifications
    notifications,
    unreadCount,
    isLoading,

    // Actions
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearNotifications,

    // Real-time updates
    addNotification,
    removeNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
